// =============================================================================
// VINUM - Catalog API
// EXACT match with Flutter implementation
// Primary endpoint: GET /availabilities/vinum?identifier={fiscalCode|vatNumber}
// =============================================================================

import { apiGet, apiPost } from './client';
import type { CatalogItem, CatalogFilter, CatalogResponse } from '../types';

// -----------------------------------------------------------------------------
// Types for Backend Response (matches Flutter exactly)
// -----------------------------------------------------------------------------

interface BackendAvailabilityResponse {
  availability: {
    warehouseName?: string;
    availableQuantity?: number;
    item?: BackendItem;
  };
  item?: BackendItem;
  resolvedPrice?: number;
  imageId?: string | null;
  wineryDefaultImageId?: string | null;
}

interface BackendHomeResponse {
  featuredItems: BackendAvailabilityResponse[];
  newArrivalsItems: BackendAvailabilityResponse[];
  featuredSectionEnabled: boolean;
  newArrivalsSectionEnabled: boolean;
  defaultImageId: string | null;
}

export interface HomePageData {
  featuredItems: CatalogItem[];
  newArrivalsItems: CatalogItem[];
  featuredSectionEnabled: boolean;
  newArrivalsSectionEnabled: boolean;
  defaultImageId: string | null;
}

interface BackendItem {
  id?: string;
  itemCode?: string;
  itemDescription?: string;
  familyDescription?: string;
  subcategoryDescription?: string;
  groupDescription?: string;
  subgroupDescription?: string;
  isBio?: boolean;
  isKm0?: boolean;
  isFeatured?: boolean;
  isNewArrival?: boolean;
  rating?: number;
  price1?: number;
  price2?: number;
  price3?: number;
  price4?: number;
}

// -----------------------------------------------------------------------------
// Catalog API Endpoints - Flutter Exact Match
// -----------------------------------------------------------------------------

/**
 * Backend paginated response format
 */
interface BackendPaginatedResponse {
  items: BackendAvailabilityResponse[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  appliedFilters?: BackendCatalogFilters;
  appliedSort?: BackendSortParams;
}

/**
 * Filter parameters for server-side filtering
 */
export interface BackendCatalogFilters {
  search?: string;
  category?: string;       // familyDescription
  subcategory?: string;    // subcategoryDescription
  group?: string;          // groupDescription (producer)
  isBio?: boolean;
  isKm0?: boolean;
  stockStatus?: 'all' | 'inStock' | 'lowStock' | 'outOfStock';
  lowStockThreshold?: number;
}

/**
 * Sort parameters for server-side sorting
 */
export interface BackendSortParams {
  sortBy?: 'name' | 'price' | 'stock' | 'category' | 'code';
  sortDirection?: 'ASC' | 'DESC';
}

/**
 * Combined query parameters for catalog API
 */
export interface CatalogQueryParams {
  page?: number;
  pageSize?: number;
  identifier?: string;
  filters?: BackendCatalogFilters;
  sort?: BackendSortParams;
}

/**
 * Categories response from /vinum/categories endpoint
 */
export interface CatalogCategoriesResponse {
  families: string[];
  subcategories: string[];
  groups: string[];
}

export const CatalogAPI = {
  /**
   * Get ALL catalog items (Flutter: single API call at init)
   * Flutter: AvailabilityApi.getAvailabilitiesVinum(identifier: identifier)
   * Endpoint: GET /availabilities/vinum?identifier={fiscalCode|vatNumber}
   *
   * NOTE: This returns ALL items - filtering is done client-side in the store
   * to match Flutter behavior exactly.
   */
  async getItems(_filter?: CatalogFilter, identifier?: string): Promise<CatalogResponse> {
    // Build URL with optional identifier (for customer-specific pricing)
    const url = identifier
      ? `/availabilities/vinum?identifier=${encodeURIComponent(identifier)}`
      : '/availabilities/vinum';

    const response = await apiGet<BackendAvailabilityResponse[]>(url);

    // Transform backend response to frontend CatalogItem[] (Flutter exact mapping)
    const items: CatalogItem[] = response
      .filter((r) => r.availability?.item || r.item)
      .map((r) => transformAvailabilityToItem(r));

    // NOTE: No filtering here - filtering is done in the store (catalog.store.ts)
    // This matches Flutter behavior where getAvailabilitiesVinum returns ALL items

    return {
      items,
      total: items.length,
      page: 1,
      limit: items.length,
      totalPages: 1,
    };
  },

  /**
   * Get catalog items with server-side pagination
   * Used for Admin panel where we need to handle large datasets efficiently
   *
   * @param page - Page number (1-based)
   * @param pageSize - Items per page (1-100)
   * @param identifier - Optional customer identifier for pricing
   */
  async getItemsPaginated(
    page: number = 1,
    pageSize: number = 20,
    identifier?: string
  ): Promise<CatalogResponse> {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('pageSize', String(pageSize));
    if (identifier) {
      params.append('identifier', identifier);
    }

    const url = `/availabilities/vinum?${params.toString()}`;
    const response = await apiGet<BackendPaginatedResponse>(url);

    // Transform backend response to frontend CatalogItem[]
    const items: CatalogItem[] = response.items
      .filter((r) => r.availability?.item || r.item)
      .map((r) => transformAvailabilityToItem(r));

    return {
      items,
      total: response.total,
      page: response.page,
      limit: response.pageSize,
      totalPages: response.totalPages,
    };
  },

  /**
   * Get catalog items with server-side pagination, filtering, search, and sorting
   * Full-featured method for Admin panel
   *
   * @param params - Query parameters including pagination, filters, and sorting
   */
  async getItemsAdvanced(params: CatalogQueryParams): Promise<CatalogResponse> {
    const urlParams = new URLSearchParams();

    // Pagination
    urlParams.append('page', String(params.page ?? 1));
    urlParams.append('pageSize', String(params.pageSize ?? 20));

    // Identifier for customer-specific pricing
    if (params.identifier) {
      urlParams.append('identifier', params.identifier);
    }

    // Filters
    if (params.filters) {
      if (params.filters.search) {
        urlParams.append('search', params.filters.search);
      }
      if (params.filters.category) {
        urlParams.append('category', params.filters.category);
      }
      if (params.filters.subcategory) {
        urlParams.append('subcategory', params.filters.subcategory);
      }
      if (params.filters.group) {
        urlParams.append('group', params.filters.group);
      }
      if (params.filters.isBio !== undefined) {
        urlParams.append('isBio', String(params.filters.isBio));
      }
      if (params.filters.isKm0 !== undefined) {
        urlParams.append('isKm0', String(params.filters.isKm0));
      }
      if (params.filters.stockStatus && params.filters.stockStatus !== 'all') {
        urlParams.append('stockStatus', params.filters.stockStatus);
      }
      if (params.filters.lowStockThreshold !== undefined) {
        urlParams.append('lowStockThreshold', String(params.filters.lowStockThreshold));
      }
    }

    // Sorting
    if (params.sort) {
      if (params.sort.sortBy) {
        urlParams.append('sortBy', params.sort.sortBy);
      }
      if (params.sort.sortDirection) {
        urlParams.append('sortDirection', params.sort.sortDirection);
      }
    }

    const url = `/availabilities/vinum?${urlParams.toString()}`;
    const response = await apiGet<BackendPaginatedResponse>(url);

    // Transform backend response to frontend CatalogItem[]
    const items: CatalogItem[] = response.items
      .filter((r) => r.availability?.item || r.item)
      .map((r) => transformAvailabilityToItem(r));

    return {
      items,
      total: response.total,
      page: response.page,
      limit: response.pageSize,
      totalPages: response.totalPages,
    };
  },

  /**
   * Get available filter options (categories, subcategories, groups)
   * Used to populate dropdown filters in the Admin UI
   */
  async getCatalogFilterOptions(): Promise<CatalogCategoriesResponse> {
    const response = await apiGet<CatalogCategoriesResponse>('/availabilities/vinum/categories');
    return response;
  },

  /**
   * Get single catalog item by ID
   * Flutter: ItemApi.getItem(id, identifier: identifier)
   * Endpoint: POST /items/{id} with body { identifier?: string }
   */
  async getItem(id: string, identifier?: string): Promise<CatalogItem> {
    const body = identifier ? { identifier } : {};
    const item = await apiPost<any>(`/items/${id}`, body);
    return transformItemToProduct(item);
  },

  /**
   * Get catalog item by itemId (alias for getItem)
   */
  async getItemByItemId(itemId: string, identifier?: string): Promise<CatalogItem> {
    return this.getItem(itemId, identifier);
  },

  /**
   * Search catalog items (client-side filtering like Flutter)
   */
  async search(query: string, limit = 20, identifier?: string): Promise<CatalogItem[]> {
    const response = await this.getItems({ q: query, limit }, identifier);
    return response.items;
  },

  /**
   * Get wine types (categories) - Flutter: extracted from groupDescription
   * Returns: 'Rosso', 'Bianco', 'Rosato', 'Altro'
   */
  async getCategories(identifier?: string): Promise<string[]> {
    const url = identifier
      ? `/availabilities/vinum?identifier=${encodeURIComponent(identifier)}`
      : '/availabilities/vinum';

    const response = await apiGet<BackendAvailabilityResponse[]>(url);
    const categories = new Set<string>();

    response.forEach((r) => {
      const item = r.availability?.item || r.item;
      if (!item) return;
      const wineType = detectWineTypeFromItem(item);
      categories.add(wineType);
    });

    // Sort with Tutti first, then alphabetically
    return Array.from(categories).sort();
  },

  /**
   * Get subcategories for a category (familyDescription)
   */
  async getSubcategories(category: string, identifier?: string): Promise<string[]> {
    const url = identifier
      ? `/availabilities/vinum?identifier=${encodeURIComponent(identifier)}`
      : '/availabilities/vinum';

    const response = await apiGet<BackendAvailabilityResponse[]>(url);
    const subcategories = new Set<string>();

    response
      .filter((r) => {
        const item = r.availability?.item || r.item;
        return item?.familyDescription === category;
      })
      .forEach((r) => {
        const item = r.availability?.item || r.item;
        if (item?.subcategoryDescription) {
          subcategories.add(item.subcategoryDescription);
        }
      });

    return Array.from(subcategories).sort();
  },

  /**
   * Get brands/producers (warehouseName - winery name in Flutter)
   */
  async getBrands(identifier?: string): Promise<string[]> {
    const url = identifier
      ? `/availabilities/vinum?identifier=${encodeURIComponent(identifier)}`
      : '/availabilities/vinum';

    const response = await apiGet<BackendAvailabilityResponse[]>(url);
    const brands = new Set<string>();

    response.forEach((r) => {
      const warehouseName = r.availability?.warehouseName;
      if (warehouseName) {
        brands.add(warehouseName);
      }
    });

    return Array.from(brands).sort();
  },

  /**
   * Get featured items (first N items)
   */
  async getFeatured(limit = 10, identifier?: string): Promise<CatalogItem[]> {
    const response = await this.getItems({ limit }, identifier);
    return response.items.slice(0, limit);
  },

  /**
   * Get new arrivals (sorted by date - Flutter doesn't have this, returns random)
   */
  async getNewArrivals(limit = 10, identifier?: string): Promise<CatalogItem[]> {
    const response = await this.getItems({ limit: limit * 2 }, identifier);
    // Shuffle and take first N (Flutter behavior)
    const shuffled = response.items.sort(() => Math.random() - 0.5);
    return shuffled.slice(0, limit);
  },

  /**
   * Get best sellers (returns featured - no sales tracking in backend)
   */
  async getBestSellers(limit = 10, identifier?: string): Promise<CatalogItem[]> {
    return this.getFeatured(limit, identifier);
  },

  /**
   * Get home page data - SINGLE optimized API call
   * Returns featured items, new arrivals, section toggles, and default image
   */
  async getHomeData(identifier?: string): Promise<HomePageData> {
    const url = identifier
      ? `/availabilities/vinum/home?identifier=${encodeURIComponent(identifier)}`
      : '/availabilities/vinum/home';

    const response = await apiGet<BackendHomeResponse>(url);

    const defaultImageId = response.defaultImageId;

    return {
      featuredItems: (response.featuredItems || [])
        .filter((r) => r.availability?.item || r.item)
        .map((r) => transformAvailabilityToItem(r, defaultImageId)),
      newArrivalsItems: (response.newArrivalsItems || [])
        .filter((r) => r.availability?.item || r.item)
        .map((r) => transformAvailabilityToItem(r, defaultImageId)),
      featuredSectionEnabled: response.featuredSectionEnabled,
      newArrivalsSectionEnabled: response.newArrivalsSectionEnabled,
      defaultImageId,
    };
  },

  /**
   * Get items by winery/producer (warehouseName)
   */
  async getByWinery(
    wineryName: string,
    page = 1,
    limit = 20,
    identifier?: string
  ): Promise<CatalogResponse> {
    const url = identifier
      ? `/availabilities/vinum?identifier=${encodeURIComponent(identifier)}`
      : '/availabilities/vinum';

    const response = await apiGet<BackendAvailabilityResponse[]>(url);

    const filtered = response.filter(
      (r) => r.availability?.warehouseName === wineryName
    );

    const items = filtered.map((r) => transformAvailabilityToItem(r));
    const startIndex = (page - 1) * limit;
    const paginatedItems = items.slice(startIndex, startIndex + limit);

    return {
      items: paginatedItems,
      total: items.length,
      page,
      limit,
      totalPages: Math.ceil(items.length / limit),
    };
  },

  /**
   * Get price range for filters (uses resolvedPrice like Flutter)
   */
  async getPriceRange(identifier?: string): Promise<{ min: number; max: number }> {
    const url = identifier
      ? `/availabilities/vinum?identifier=${encodeURIComponent(identifier)}`
      : '/availabilities/vinum';

    const response = await apiGet<BackendAvailabilityResponse[]>(url);

    const prices = response
      .map((r) => r.resolvedPrice)
      .filter((p): p is number => typeof p === 'number' && p > 0);

    if (prices.length === 0) {
      return { min: 0, max: 100 };
    }

    return {
      min: Math.min(...prices),
      max: Math.max(...prices),
    };
  },
};

// -----------------------------------------------------------------------------
// Helper Functions - Flutter Exact Match
// -----------------------------------------------------------------------------

/**
 * Extract item ID from backend item.
 * Backend always returns `id` (UUID primary key). Falls back to `itemCode` for safety.
 */
function extractItemId(item: BackendItem): string {
  return item.id || item.itemCode || '';
}

/**
 * Detect wine type from multiple descriptive fields.
 *
 * Match strategy:
 *  - cerca in groupDescription, familyDescription, subcategoryDescription,
 *    subgroupDescription, itemDescription (in quest'ordine)
 *  - usa match a prefisso (es. "ross" copre "rosso"/"rossi"/"rossa")
 *    per gestire singolare/plurale/maschile/femminile.
 *  - "rose" e' considerato Rosato (varianti es. "rosé", "rose'")
 */
function detectWineType(...fields: (string | undefined | null)[]): string {
  const haystack = fields
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .join(' ')
    .toLowerCase();

  if (!haystack) return 'Altro';

  // Rosato controllato prima di Rosso/Bianco: "rosato" contiene "rosa" ma non
  // deve essere classificato come Rosso. (rosat[oaie] non collide con ross[oaie]
  // perche' ross richiede doppia s, ma teniamo l'ordine per chiarezza.)
  if (/rosat[oaie]|\bros[eé]\b/.test(haystack)) return 'Rosato';
  if (/ross[oaie]/.test(haystack)) return 'Rosso';
  if (/bianc(?:h[oaie]|[oaie])/.test(haystack)) return 'Bianco';

  return 'Altro';
}

/**
 * Determina il tipo di vino di un Item usando SOLO i campi di categoria
 * strutturati provenienti da TeamSystem (gruppo/sottogruppo/sottocategoria,
 * es. "VINI BIANCHI", "VINI ROSSI").
 *
 * NON si usano `familyDescription` (= cantina/produttore) né `itemDescription`
 * (nome libero del vino): contengono spesso parole come "rosa"/"rossi" o nomi
 * di cantine ("Le Rose", "Rossi") che facevano finire il vino nel colore
 * sbagliato — la causa della classificazione apparentemente "a caso".
 */
export function detectWineTypeFromItem(item: any): string {
  return detectWineType(
    item?.groupDescription,
    item?.subgroupDescription,
    item?.subcategoryDescription,
  );
}

/**
 * Transform backend availability response to frontend CatalogItem
 * Flutter: _catalogToProduct(Map<String, dynamic> catalog)
 */
function buildImageUrl(imageId?: string | null, wineryDefaultImageId?: string | null, defaultImageId?: string | null): string | undefined {
  const id = imageId || wineryDefaultImageId || defaultImageId;
  if (!id) return undefined;
  return `/media/file/${id}/medium`;
}

function transformAvailabilityToItem(response: BackendAvailabilityResponse, defaultImageId?: string | null): CatalogItem {
  const rawItem = (response.availability?.item || {}) as any;
  const normalizedItem = (response.item || {}) as any;
  const item = { ...rawItem, ...normalizedItem } as any;
  if (item.isKm0 === undefined && rawItem.isKmo !== undefined) {
    item.isKm0 = rawItem.isKmo;
  }
  const availability = response.availability || {};

  const itemId = extractItemId(item);
  const wineType = detectWineTypeFromItem(item);


  const brand = (item.familyDescription || availability.warehouseName || 'Cantina')
    .toString()
    .trim();

  // Id univoco PER RIGA del catalogo (= per Availability), non per Item.
  // Da quando il catalogo mostra tutte le righe Availabilities di tutti i
  // magazzini, lo stesso Item puo' apparire in N coppie (Item, Cantina)
  // diverse. Usare itemId come React key fonderebbe le card sullo stesso
  // chiave -> il filtro produttore non sembra cambiare la vista.
  const availabilityId = (availability as any)?.id ? String((availability as any).id) : null;
  const rowId = availabilityId
    ? `${itemId}__${availabilityId}`
    : `${itemId}__${brand.toLowerCase().replace(/\s+/g, '_')}`;

  return {
    id: rowId,
    itemId: itemId,
    itemCode: item.itemCode || '',
    itemDescription: item.itemDescription || 'Senza nome',
    category: wineType,
    subcategory: item.subcategoryDescription || '',
    brand,
    wineryId: availability.warehouseName || '',
    warehouseName: (availability.warehouseName || '').toString().trim() || undefined,
    price1: response.resolvedPrice ?? item.price1 ?? 0,
    price2: item.price2,
    price3: response.resolvedPrice ?? item.price3,
    price4: item.price4,
    pricePrivate: toNumberOrUndefined(item.price3),
    priceCompany: toNumberOrUndefined(item.price1),
    availableQuantity: availability.availableQuantity ?? 0,
    inCatalog: true,
    imageUrl: buildImageUrl(response.imageId, response.wineryDefaultImageId, defaultImageId),
    imageId: response.imageId ?? undefined,
    isFeatured: item.isFeatured ?? false,
    isNewArrival: item.isNewArrival ?? false,
    specs: {
      isBio: item.isBio ?? false,
      isKm0: item.isKm0 ?? false,
      rating: item.rating,
      wineType: wineType,
      groupDescription: item.groupDescription,
    },
  };
}

/**
 * TypeORM serializza i campi DECIMAL come stringa (preservare precisione).
 * Converte string|number|null|undefined → number|undefined.
 */
function toNumberOrUndefined(v: unknown): number | undefined {
  if (v === null || v === undefined || v === '') return undefined;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Transform backend item object to frontend CatalogItem
 * Used for single item fetch via POST /items/:id
 */
function transformItemToProduct(item: any): CatalogItem {
  const availability = item.availabilities?.[0] || {};
  const itemId = extractItemId(item);
  const wineType = detectWineTypeFromItem(item);

  // Backend returns computed price as `price` field
  const resolvedPrice = item.price ?? item.price1 ?? 0;

  // Produttore/cantina: VINUM-ART colonna E = Item.familyDescription.
  // Coerente con transformAvailabilityToItem. Fallback su warehouse/gruppo.
  const brand = (item.familyDescription || availability.warehouseName || item.groupDescription || 'Cantina')
    .toString()
    .trim();

  return {
    id: itemId,
    itemId: itemId,
    itemCode: item.itemCode || '',
    itemDescription: item.itemDescription || 'Senza nome',
    category: wineType,
    subcategory: item.subcategoryDescription || '',
    brand,
    wineryId: availability.warehouseName || item.groupDescription || '',
    price1: resolvedPrice,
    price2: item.price2,
    price3: resolvedPrice,
    price4: item.price4,
    // Listino Privato/Azienda invertito: il Privato usa price3, l'Azienda price1.
    pricePrivate: toNumberOrUndefined(item.price3),
    priceCompany: toNumberOrUndefined(item.price1),
    availableQuantity: availability.availableQuantity ?? 0,
    inCatalog: true,
    imageUrl: buildImageUrl(item.imageId),
    imageId: item.imageId ?? undefined,
    specs: {
      isBio: item.isBio ?? false,
      isKm0: item.isKm0 ?? false,
      rating: item.rating,
      wineType: wineType,
      groupDescription: item.groupDescription,
    },
  };
}

// NOTE: Client-side filtering is now handled in catalog.store.ts (computeFiltered function)
// This matches Flutter behavior where filtering happens in the StatefulWidget, not in the API layer
